import type { MultiUserPickerOption } from "@/shared/components/multi-user-picker/multi-user-picker-list";
import type { ApiError } from "@/shared/lib/api-call";
import type { DraftContext } from "@/shared/writing-assistant/writing-assistant.schema";

/// Pure helpers extracted from `create-kudos-dialog.tsx` so the dialog file
/// itself stays under A3's 250-line ceiling. No JSX here, anything visual
/// belongs in a co-located component file.

/// Curated suggestions for value tags. Single tap appends to the form
/// array. Aligns with the Pulsewise values we already surface elsewhere
/// (relationship board, brand voice). Free typing still allowed.
export const SUGGESTED_VALUE_TAGS: ReadonlyArray<string> = [
  "teamwork",
  "ownership",
  "growth",
  "kindness",
  "craft",
];

/// useFormPref descriptor for the last-used value tags (smart default, F6).
export const LAST_VALUE_TAGS = {
  key: "kudos:last-value-tags",
  version: 1,
  fallback: [] as ReadonlyArray<string>,
  validate: (v: unknown): v is ReadonlyArray<string> =>
    Array.isArray(v) && v.every((t) => typeof t === "string"),
};

type PeopleRow = {
  status: string;
  userId: string;
  displayName: string;
  email: string;
  avatarUrl: string | null;
};

/// Convert People-directory rows into kudos recipient picker options. Only
/// people who have joined are offered. Excludes self (spec self-recognition
/// rule) and any legacy invitation row without a userId.
export function toKudosRecipientOptions(
  rows: ReadonlyArray<PeopleRow>,
  selfId: string | undefined,
): MultiUserPickerOption[] {
  return rows
    .filter((r) => r.status === "active" && Boolean(r.userId) && r.userId !== selfId)
    .map((r) => ({
      userId: r.userId,
      displayName: r.displayName,
      email: r.email,
      avatarUrl: r.avatarUrl,
      pending: false,
    }));
}

/// Build the writing-assistant context snapshot for a kudos draft: recipient
/// first names (resolved via the dialog's id->option map) plus trimmed
/// non-empty value tags. Both lists are capped at 10 to match the boundary
/// schema. Returns undefined when there is nothing to send.
export function buildKudosDraftContext(
  recipientIds: ReadonlyArray<string>,
  recipientById: ReadonlyMap<string, MultiUserPickerOption>,
  rawValueTags: ReadonlyArray<string>,
): DraftContext | undefined {
  const firstNames = recipientIds
    .map((id) => recipientById.get(id)?.displayName.split(" ")[0])
    .filter((n): n is string => Boolean(n))
    .slice(0, 10);
  const tags = rawValueTags
    .map((t) => t.trim())
    .filter((t) => t.length > 0)
    .slice(0, 10);
  const ctx = {
    ...(firstNames.length > 0 ? { recipientFirstNames: firstNames } : {}),
    ...(tags.length > 0 ? { valueTags: tags } : {}),
  };
  return Object.keys(ctx).length > 0 ? ctx : undefined;
}

/** True when shore returns 400 VALIDATION_ERROR with details.code =
 *  'SELF_KUDOS_NOT_ALLOWED'. In practice unreachable (the picker excludes
 *  self), defence in depth for edge races. */
export function isSelfKudosError(err: ApiError): boolean {
  if (err.status !== 400) return false;
  if (!err.details || typeof err.details !== "object") return false;
  const code = (err.details as { code?: unknown }).code;
  return code === "SELF_KUDOS_NOT_ALLOWED";
}

/** "Ada, Maya and 3 more", turns the resolved display-name list into a
 *  natural toast string. */
export function formatRecipientNames(names: ReadonlyArray<string>): string {
  if (names.length === 0) return "your teammate";
  if (names.length === 1) return names[0] ?? "";
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names[0]}, ${names[1]} and ${names.length - 2} more`;
}

/** Two-letter avatar fallback initials. Empty / whitespace name → "?". */
export function initialsOf(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "?";
  if (parts.length === 1) return parts[0]?.charAt(0).toUpperCase() ?? "?";
  return `${parts[0]?.charAt(0) ?? ""}${parts[parts.length - 1]?.charAt(0) ?? ""}`.toUpperCase();
}
